import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ArrowRight, MessageCircle, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { lookupOrder } from '@/lib/api';
import { cn } from '@/lib/utils';

const WHATSAPP_NUMBER = "3501770286";

const OrderLookup = () => {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const validateForm = () => {
    const newErrors = {};
    
    if (!orderNumber.trim()) {
      newErrors.orderNumber = 'Inserisci il numero ordine';
    }
    
    if (!email.trim()) {
      newErrors.email = 'Inserisci l\'email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Email non valida';
    }
    
    if (!phone.trim()) {
      newErrors.phone = 'Inserisci il numero di telefono';
    } else if (phone.replace(/\D/g, '').length < 8) {
      newErrors.phone = 'Numero di telefono non valido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const order = await lookupOrder(orderNumber, email, phone);
      // Reset failed attempts on success
      setFailedAttempts(0);
      // Store order in sessionStorage for subsequent pages
      sessionStorage.setItem('currentOrder', JSON.stringify(order));
      navigate(`/order/${order.order_number}`);
    } catch (error) {
      const newFailedAttempts = failedAttempts + 1;
      setFailedAttempts(newFailedAttempts);
      
      // Different error messages based on error type
      if (error.response?.status === 401) {
        toast.error('I dati inseriti non corrispondono all\'ordine. Verifica email o telefono.');
      } else if (error.response?.status === 404) {
        toast.error('Ordine non trovato. Verifica il numero ordine.');
      } else {
        toast.error('Si è verificato un errore. Riprova più tardi.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent(`Ciao, ho bisogno di assistenza per il mio ordine ${orderNumber || ''}`);
    window.open(`https://wa.me/39${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  return (
    <div className="page-container bg-white">
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-6 py-12 md:py-24">
        <motion.div 
          className="max-w-md w-full"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="font-heading text-4xl md:text-5xl font-normal tracking-tight mb-4">
              Resi e Cambi
            </h1>
            <p className="text-base text-muted-foreground font-light leading-relaxed">
              Inserisci i dati del tuo ordine per iniziare la procedura di reso o cambio.
            </p>
          </motion.div>
          
          <motion.form 
            onSubmit={handleSubmit}
            className="space-y-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Order Number */}
            <div>
              <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                Numero Ordine <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={orderNumber}
                  onChange={(e) => {
                    setOrderNumber(e.target.value);
                    if (errors.orderNumber) setErrors(prev => ({...prev, orderNumber: null}));
                  }}
                  placeholder="Es. DForder_12345"
                  className={cn(
                    "luxury-input w-full pr-10",
                    errors.orderNumber && "border-red-500 focus:ring-red-500"
                  )}
                  data-testid="order-input"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              </div>
              {errors.orderNumber && (
                <p className="text-red-500 text-xs mt-1">{errors.orderNumber}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                Email <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors(prev => ({...prev, email: null}));
                  }}
                  placeholder="La tua email"
                  className={cn(
                    "luxury-input w-full pr-10",
                    errors.email && "border-red-500 focus:ring-red-500"
                  )}
                  data-testid="email-input"
                />
                <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Phone */}
            <div>
              <label className="text-xs font-bold tracking-[0.15em] uppercase text-muted-foreground mb-2 block">
                Telefono <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (errors.phone) setErrors(prev => ({...prev, phone: null}));
                  }}
                  placeholder="Il tuo numero di telefono"
                  className={cn(
                    "luxury-input w-full pr-10",
                    errors.phone && "border-red-500 focus:ring-red-500"
                  )}
                  data-testid="phone-input"
                />
                <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              </div>
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
              )}
            </div>

            <p className="text-xs text-muted-foreground text-center">
              Inserisci l'email e il telefono utilizzato per l'ordine. Tutti i campi sono obbligatori.
            </p>
            
            <Button 
              type="submit"
              disabled={isLoading}
              className="luxury-btn-primary w-full flex items-center justify-center gap-3"
              data-testid="search-btn"
            >
              {isLoading ? (
                'Verifica in corso...'
              ) : (
                <>
                  Accedi
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </Button>
          </motion.form>

          {/* WhatsApp Help - shown after 2 failed attempts */}
          {failedAttempts >= 2 && (
            <motion.div
              className="mt-8 p-6 border border-emerald-200 bg-emerald-50 text-center"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <p className="text-sm text-emerald-800 mb-4">
                Hai difficoltà ad accedere? Contatta la nostra assistenza su WhatsApp
              </p>
              <Button 
                type="button"
                onClick={openWhatsApp}
                className="bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-2"
                data-testid="whatsapp-help-btn"
              >
                <MessageCircle className="w-5 h-5" />
                Assistenza WhatsApp
              </Button>
            </motion.div>
          )}
          
          <motion.p 
            className="text-xs text-muted-foreground mt-8 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Trovi il numero ordine nella email di conferma oppure nella conferma d'ordine su WhatsApp
          </motion.p>
        </motion.div>
      </main>
      
      {/* Admin Link */}
      <footer className="p-6 text-center border-t border-border">
      </footer>
    </div>
  );
};

export default OrderLookup;
